import React from "react";
import { DecoratedText } from "@hopper/decorated_text";
import pluralize from "pluralize";

import { RequestedTest, TestEnvironmentPlatform } from "types/graphql";
import { Field } from "./field";
import {
  getActivityTitle,
  getApplicationTypeLabel,
} from "components/flexible_testing/test_request_page/requested_test_page/helpers";
import { Activity } from "components/flexible_testing/test_request_page/requested_test_page/activity_step_content";

type RequestedTestDetailsProps = {
  requestedTest: RequestedTest;
};

const TestField = ({ label, value }: { label?: string; value: string | number | null | undefined }) => (
  <div>
    {label && <span className="font-bold">{label}: </span>}
    <DecoratedText text={value?.toString() || ""} />
  </div>
);

const TestBooleanField = ({ label, value }: { label: string; value: boolean }) => (
  <div>
    <span className="font-bold">{label}: </span>
    <DecoratedText text={value ? "Yes" : "No"} />
  </div>
);

export const RequestedTestDetails = ({ requestedTest }: RequestedTestDetailsProps) => {
  const testCasesPresent = requestedTest.testCasesCount && requestedTest.testCasesCount > 0;

  return (
    <Field label={requestedTest.name} dataTestid="requested-test">
      <div className="grid grid-cols-12">
        <div className="col-span-6">
          <TestField label="Access to application URL" value={requestedTest.accessToApplicationUrl} />
          <TestField label="Activity" value={getActivityTitle(requestedTest.activity as Activity)} />
          <TestField
            label="Platform"
            value={getApplicationTypeLabel(requestedTest.applicationType as TestEnvironmentPlatform)}
          />
          {requestedTest.activity !== "test_case_run" && (
            <>
              <TestField label="Scope" value={requestedTest.inScope} />
              <TestField label="Outside of scope" value={requestedTest.outOfScope} />
            </>
          )}
          {testCasesPresent && (
            <>
              <TestField
                label="Number of results per test case needed"
                value={requestedTest.numberOfResultsPerTestCaseNeeded}
              />
              <TestField
                label="Test cases count"
                value={`${requestedTest.testCasesCount} ${pluralize("test case", requestedTest.testCasesCount || 0)}`}
              />
            </>
          )}
          <TestBooleanField
            label="Access to secure environment"
            value={requestedTest.requiresAccessToSecureEnvironment}
          />
          {requestedTest.requiresAccessToSecureEnvironment && (
            <div>
              <TestField value={requestedTest.accessToSecureEnvironmentInstructions} />
            </div>
          )}
          <TestBooleanField
            label="Requires predefined user accounts"
            value={requestedTest.requiresPredefinedUserAccounts}
          />
          {requestedTest.requiresPredefinedUserAccounts && (
            <div>
              <TestField value={requestedTest.predefinedUserAccountsInstructions} />
            </div>
          )}
          <TestBooleanField
            label="Requires financial transactions"
            value={requestedTest.requiresFinancialTransactions}
          />
          {requestedTest.requiresFinancialTransactions && (
            <div>
              <TestField value={requestedTest.financialTransactionsInstructions} />
            </div>
          )}
          <TestBooleanField label="Requires KYC identity check" value={requestedTest.requiresKycIdentityCheck} />
          {requestedTest.requiresKycIdentityCheck && (
            <div>
              <TestField value={requestedTest.kycIdentityCheckInstructions} />
            </div>
          )}
          <TestBooleanField
            label="Requires Personal Identifying Information"
            value={requestedTest.requiresPersonalIdentifyingInformation}
          />
          {requestedTest.requiresPersonalIdentifyingInformation && (
            <div>
              <TestField value={requestedTest.personalIdentifyingInformationInstructions} />
            </div>
          )}
          <TestBooleanField
            label="Requires moving to a different location"
            value={requestedTest.requiresMovingToDifferentLocation}
          />
          {requestedTest.requiresMovingToDifferentLocation && (
            <div>
              <TestField value={requestedTest.movingToDifferentLocationInstructions} />
            </div>
          )}
        </div>
      </div>
    </Field>
  );
};
